import MarginDecoration from '../../common/components/MarginDecoration/MarginDecoration'
import PageLayout from '../../common/components/PageLayout/PageLayout'
import PageLink from '../../common/components/PageLink/PageLink'
import References from '../../common/components/References/References'
import TextRef from '../../common/components/TextRef/TextRef'
import TextSeparator from '../../common/components/TextSeparator/TextSeparator'
import Title from '../../common/components/Title/Title'
import './Implementation.css'

function Implementation () {
  return (
    <PageLayout title="Implementation">
      <MarginDecoration />
      <Title
        isSub
        title="Introductory context to blood donation regulation in Québec"
      />
      <p>
        In Canada, in 1992, thousands of people were exposed to HIV and
        hepatitis C <TextRef sup="1" content="through contaminated blood" />.
        This led to the implementation of regulation enforced by Héma-Québec in
        1998 in the province of <TextRef sup="2" content="Quebec" />.
        Héma-Québec&apos;s main goals are to ensure sufficient blood and blood
        products supplies to health services and the general population as per
        the act respecting the Héma-Québec and the biovigilance
        <TextRef sup="3" content="committee" />. During the project, we
        collaborated with Héma-Québec and discussed the achievability of the
        project and to ensure that it respected regulations in place and be
        easily implemented within the blood donation process.
      </p>
      <TextSeparator />
      <Title
        isSub
        title="Proposed implementation : Integrating Blood Cell Barber Shop"
      />
      <p>
        In our project <i>Blood Cell Barber Shop</i>, we proposed a fluidic
        tubular device for enzymatic removal of blood antigens. In the device,
        enzymes specific to ABO-type blood antigens would be coated in the inner
        layer of the device. Any blood donation would pass through the system to
        obtain O-type blood. Here, we designed the device by testing different
        combinations of geometry in order to obtain an optimal flow and
        homogeneity, maximizing the enzymatic degradation.
      </p>
      <p>
        In collaboration with Héma-Québec and potentially with other medical
        instances, we would like to design the final device in a modular manner
        to fit with existing blood donation processing equipment. This
        implementation of our project would serve to relieve the demand of
        uncommon blood types felt by blood banks all over the world.
      </p>
      <TextSeparator />
      <Title
        isSub
        title="Blood Cell Barber Shop's impact on the scientific world?"
      />
      <p>
        The project we designed for ABO-type blood processing is innovative for
        its implementation into a fluidic device. Such a device was developed in
        previous reports for cell-free biosynthesis of interesting compounds
        such as <TextRef sup="4" content="amorphadiene" />. In that regard, the
        implementation of enzymatic removal of blood antigens in such devices
        would prove to be more efficient than current strategy. This device
        would also provide a system preventing the occurrence of bacterial
        proteins in the processed blood product. This is in consideration of
        Héma-Québec concern of possible medical problems related to residual
        bacterial proteins.
      </p>
      <p>
        Furthermore, the GH110b enzyme we used has a broader{' '}
        <TextRef sup="5" content="activity" />. This could allow for the
        extension of our device to xenotransplantation application.
      </p>
      <p>
        The extensive dry lab work we completed on Rh protein complex modelling
        and assembly also represents the very first 3D model of this important
        complex. The lack of an available predicted structure for this complex
        made research on avenues to target it difficult if not impossible. The
        results we obtained here therefore open the door to much needed research
        on ways to target and inactivate this complex&apos;s immunogenic
        properties in order to truly be able to convert blood donations into
        universal blood.
      </p>
      <p>
        Our combined efforts on ABO-type antigens and Rh protein modelling
        provide a very solid base on which to continue researching avenues to
        treat blood and convert blood donations of any blood type into O- type
        blood.
      </p>
      <TextSeparator />
      <Title isSub title="The project's limitations" />
      <p>
        Obviously, protein stability would be a serious limitation for the
        economic viability and relevance of the device. In that regard, we were
        not able to test experimentally the optimal stabilizing conditions, the
        shelf-life of our device and its reusability. Work should be done
        accordingly for validation. If protein instability is at stake,
        mutagenesis or even identifying new enzymes could resolve that concern.
      </p>
      <p>
        Our fluidic analyses remain preliminary. While these simulations remain
        accurate and efficient for the purpose of determining which parameters
        significantly influence the flow homogeneity of the blood, other 3D
        simulations using more advanced models will be necessary.
      </p>
      <ul className="implementation-ul">
        <li>
          Adapting <i>Blood Cell Barber Shop</i> to blood bank storage
          conditions : our system could be optimized to work at low temperature,
          more precisely at blood storage conditions (around 4 °C).
        </li>
        <li>
          Design the fluidic system using our simulation&apos;s parameters (See{' '}
          <PageLink src="/ulaval/model">project modelling</PageLink>).
        </li>
        <li>Test the antigen-removal system using clinical blood samples.</li>
      </ul>
      <References
        references={[
          <span key={1}>
            <i>
              https://ici.radio-canada.ca/nouvelle/1091711/hema-quebec-sang-contamine-canada-indemnisation-archives
            </i>
          </span>,
          <span key={2}>
            <i>
              https://www.hema-quebec.qc.ca/publications/communiques/archives/1998/communiques/1998-09-28.fr.html
            </i>
          </span>,
          <span key={3}>
            <i>
              https://www.legisquebec.gouv.qc.ca/en/document/cs/H-1.1?fbclid=IwAR2_s4pHaokD46xZMc-S-Mx_0osh4rzp-dvmPFfHFrPGiaELw0iPKiWbNgg
            </i>
          </span>,
          <span key={4}>
            Kabernick DC, Gostick JT, Ward VCA. Kinetic characterization and
            modeling of sequentially entrapped enzymes in 3D-printed PMMA
            microfluidic reactors for the synthesis of amorphadiene via the
            isopentenol utilization pathway. Biotechnol Bioeng. 2022
            May;119(5):1239-1251. doi: 10.1002/bit.28046. Epub 2022 Feb 9. PMID:
            35099806.
          </span>,
          <span key={5}>
            Liu QP, Yuan H, Bennett EP, Levery SB, Nudelman E, Spence J, Pietz
            G, Saunders K, White T, Olsson ML, Henrissat B, Sulzenbacher G,
            Clausen H. Identification of a GH110 subfamily of alpha
            1,3-galactosidases: novel enzymes for removal of the alpha 3Gal
            xenotransplantation antigen. J Biol Chem. 2008 Mar
            28;283(13):8545-54. doi: 10.1074/jbc.M709020200. Epub 2008 Jan 28.
            PMID: 18227066; PMCID: PMC2417185.
          </span>
        ]}
      />
    </PageLayout>
  )
}

export default Implementation
